import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { registerAutoAcceptConceptGroup } from '@verantos-dev/app-state-cohort-builder';
import { ResearchAssistantSocketProvider } from '@verantos-dev/feature-research-assistant';

import { useAutoAcceptConceptGroupProvider } from '../hooks/common/useAutoAcceptConceptGroupProvider';
import { useResearchAssistantCohortCallbacks } from '../hooks/useResearchAssistantCohortCallbacks';

/**
 * Layout route for :cohortVersionId that holds the socket provider.
 * Both CohortBuilderPage and AnalyticalVariableEditPage share this
 * provider via <Outlet />, preventing socket disconnect/reconnect
 * when navigating between sibling routes.
 */
export const CohortVersionLayout = () => {
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false);
  const autoAccept = useAutoAcceptConceptGroupProvider();
  const {
    onCohortDslReceived,
    onPartialCohortDslReceived,
    onConceptGroupConfidence,
    onClearConceptGroupConfidence,
    onMarkConceptGroupAsGenerated,
    onConceptGroupSelections,
  } = useResearchAssistantCohortCallbacks();

  // Register the auto-accept callback so it's accessible from the RA chat panel
  // (which is a sibling in the component tree, not a child of CohortVersionLayout).
  useEffect(() => {
    registerAutoAcceptConceptGroup(autoAccept);
    return () => {
      registerAutoAcceptConceptGroup(null);
    };
  }, [autoAccept]);

  if (isGen3Enabled) {
    return <Outlet />;
  }

  return (
    <ResearchAssistantSocketProvider
      onCohortDslReceived={onCohortDslReceived}
      onPartialCohortDslReceived={onPartialCohortDslReceived}
      onConceptGroupConfidence={onConceptGroupConfidence}
      onClearConceptGroupConfidence={onClearConceptGroupConfidence}
      onMarkConceptGroupAsGenerated={onMarkConceptGroupAsGenerated}
      onConceptGroupSelections={onConceptGroupSelections}
    >
      <Outlet />
    </ResearchAssistantSocketProvider>
  );
};
